import { supabase } from "@/integrations/supabase/client";
import { ConversationStatus, isValidStatus } from "@/types/conversation";

export interface StatusUpdateResponse {
  success: boolean;
  error?: string;
}

export const statusService = {
  /**
   * Update the status of a conversation
   */
  async updateConversationStatus(
    conversationId: string,
    newStatus: ConversationStatus
  ): Promise<StatusUpdateResponse> {
    try {
      if (!isValidStatus(newStatus)) {
        return {
          success: false,
          error: "Invalid status value"
        };
      }

      const { error } = await supabase
        .from("conversations")
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq("id", conversationId);

      if (error) {
        console.error("Error updating conversation status:", error);
        return {
          success: false,
          error: error.message
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Failed to update conversation status:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  },

  /**
   * Get conversations by status
   */
  async getConversationsByStatus(
    organizationId: string,
    statuses: ConversationStatus[]
  ) {
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          id,
          created_at,
          updated_at,
          status,
          state,
          priority,
          intent,
          summary,
          sentiment,
          tags,
          user_type,
          estimated_time_saved,
          agent_id,
          agent_chat_user_id,
          agents (
            agent_name,
            avatar_url,
            title,
            organization_id
          ),
          agent_chat_users (
            name,
            email,
            phone,
            is_anonymous
          )
        `)
        .in("status", statuses)
        .eq("agents.organization_id", organizationId)
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Error fetching conversations by status:", error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch conversations by status:", error);
      return {
        data: null,
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  },

  /**
   * Bulk update status for multiple conversations
   */
  async bulkUpdateStatus(
    conversationIds: string[],
    newStatus: ConversationStatus
  ): Promise<StatusUpdateResponse> {
    try {
      // Validate that conversationIds array is not empty
      if (!conversationIds || conversationIds.length === 0) {
        return {
          success: false,
          error: "No conversation IDs provided"
        };
      }

      if (!isValidStatus(newStatus)) {
        return {
          success: false,
          error: "Invalid status value"
        };
      }

      const { error } = await supabase
        .from("conversations")
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .in("id", conversationIds);

      if (error) {
        console.error("Error bulk updating conversation status:", error);
        return {
          success: false,
          error: error.message
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Failed to bulk update conversation status:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  },

  /**
   * Get status distribution for analytics
   */
  async getStatusDistribution(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          status,
          agents!inner (organization_id)
        `)
        .eq("agents.organization_id", organizationId);

      if (error) {
        console.error("Error fetching status distribution:", error);
        return { data: null, error: error.message };
      }

      // Count by status
      const distribution = data.reduce((acc, conv) => {
        const status = conv.status || 'open';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return { data: distribution, error: null };
    } catch (error) {
      console.error("Failed to fetch status distribution:", error);
      return {
        data: null,
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }
}; 